import { useI18n } from '@ecom/i18n';
import {
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@ecom/ui-kit';
import { type ReactElement } from 'react';

import type { CategoryTreeRow } from '../types';

/**
 * "Liste" — Phase 1. One row per real category (`flattenCategoryTree`,
 * never a fabricated one), indented by its real depth in the tree so the
 * parent/child relationship reads at a glance without a separate diagram.
 */
export function CategoryTable({
  rows,
  parentNameById,
  onEdit,
  onDelete,
  deletingId,
}: {
  rows: readonly CategoryTreeRow[];
  parentNameById: ReadonlyMap<string, string>;
  onEdit: (row: CategoryTreeRow) => void;
  onDelete: (row: CategoryTreeRow) => void;
  deletingId: string | null;
}): ReactElement {
  const { t } = useI18n();
  return (
    <Table caption={t('merchant.nav.categories')}>
      <TableHead>
        <TableRow>
          <TableHeaderCell>{t('merchant.categories.form.nameLabel')}</TableHeaderCell>
          <TableHeaderCell>{t('merchant.store.form.slugLabel')}</TableHeaderCell>
          <TableHeaderCell>{t('merchant.categories.table.colParent')}</TableHeaderCell>
          <TableHeaderCell align="end">
            {t('merchant.categories.table.colProducts')}
          </TableHeaderCell>
          <TableHeaderCell>{t('merchant.orders.table.colStatus')}</TableHeaderCell>
          <TableHeaderCell> </TableHeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map(({ category, depth, productCount }) => (
          <TableRow key={category.id}>
            <TableCell>
              <div
                className="flex items-center gap-1.5"
                style={{ marginInlineStart: `${String(depth * 1.5)}rem` }}
              >
                {depth > 0 ? (
                  <span className="text-inkFaint" aria-hidden="true">
                    ↳
                  </span>
                ) : null}
                <span className="text-sm font-medium text-ink">{category.name}</span>
              </div>
            </TableCell>
            <TableCell>
              <span className="font-mono text-xs text-inkFaint">{category.slug}</span>
            </TableCell>
            <TableCell>
              <span className="text-sm text-inkFaint">
                {category.parentCategoryId !== null
                  ? (parentNameById.get(category.parentCategoryId) ?? '—')
                  : '—'}
              </span>
            </TableCell>
            <TableCell align="end">
              <span className="font-mono text-sm tabular-nums text-ink">{productCount}</span>
            </TableCell>
            <TableCell>
              <Badge tone={category.isActive ? 'good' : 'neutral'}>
                {category.isActive
                  ? t('merchant.categories.active')
                  : t('merchant.categories.inactive')}
              </Badge>
            </TableCell>
            <TableCell align="end">
              <div className="flex items-center justify-end gap-3 text-xs">
                <button
                  type="button"
                  onClick={() => {
                    onEdit({ category, depth, productCount });
                  }}
                  className="font-medium text-accent hover:underline"
                >
                  {t('merchant.common.edit')}
                </button>
                <button
                  type="button"
                  disabled={deletingId === category.id}
                  onClick={() => {
                    onDelete({ category, depth, productCount });
                  }}
                  className="font-medium text-critical hover:underline disabled:opacity-50"
                >
                  {t('merchant.common.delete')}
                </button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
